import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Layout, Playlist } from '@ghostscreen/contracts';
import { layoutHasTicker } from '@ghostscreen/contracts';
import { ConfirmDialog } from '../../components/dashboard/ConfirmDialog';
import { apiErrorMessage } from '../../i18n';
import { api } from '../../lib/api';
import { useTheme } from '../../theme/ThemeProvider';
import { templateDef, zoneName } from './layoutFormat';
import { LayoutPreviewGrid } from './LayoutPreviewGrid';

type LayoutDrawerProps = {
  open: boolean;
  organisationId: string;
  layout: Layout | null;
  playlists: Playlist[];
  onClose: () => void;
};

/**
 * Edit layout drawer — Ghost Screen Layouts.dc.html.
 * Name, zone playlists, and ticker save as you go.
 */
export function LayoutDrawer({
  open,
  organisationId,
  layout,
  playlists,
  onClose,
}: LayoutDrawerProps) {
  const { t } = useTranslation(['layouts', 'common']);
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [ticker, setTicker] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !layout) return;
    setName(layout.name);
    setTicker(layout.tickerText);
    setError(null);
    setConfirmDelete(false);
  }, [open, layout]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['layouts', organisationId] });
  };

  const save = useMutation({
    mutationFn: (body: {
      name?: string;
      tickerText?: string;
      zones?: Record<string, string | null>;
    }) => {
      if (!layout) throw new Error('No layout');
      return api.updateLayout(organisationId, layout.id, body);
    },
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, t('drawer.saveError'))),
  });

  const remove = useMutation({
    mutationFn: () => {
      if (!layout) throw new Error('No layout');
      return api.deleteLayout(organisationId, layout.id);
    },
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, t('drawer.deleteError'))),
  });

  if (!open || !layout) return null;

  const def = templateDef(layout.template);
  const hasTicker = layoutHasTicker(layout.template);
  const playlistName = (id: string | null) =>
    id ? playlists.find((playlist) => playlist.id === id)?.name ?? null : null;

  const previewLabels: Record<string, string> = {};
  for (const zone of def.zones) {
    if (zone.ticker) {
      previewLabels.Ticker = ticker.trim() || t('preview.tickerPlaceholder');
      continue;
    }
    const assigned = layout.zones.find((item) => item.key === zone.key)?.playlistId ?? null;
    previewLabels[zone.key] =
      playlistName(assigned) || t('preview.emptyZone', { zone: zoneName(zone.key) });
  }

  const commitName = () => {
    const trimmed = name.trim() || t('list.untitled');
    if (trimmed === layout.name) {
      setName(layout.name);
      return;
    }
    setName(trimmed);
    save.mutate({ name: trimmed });
  };

  const commitTicker = () => {
    if (ticker === layout.tickerText) return;
    save.mutate({ tickerText: ticker });
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[100] flex justify-end"
        style={{ background: 'rgba(4,4,6,0.58)' }}
        onClick={onClose}
      >
        <aside
          data-theme={theme}
          role="dialog"
          aria-modal="true"
          aria-label={t('drawer.dialogLabel')}
          className="flex h-full w-[min(500px,100vw)] flex-col border-s shadow-[-24px_0_60px_rgba(0,0,0,0.35)] rtl:shadow-[24px_0_60px_rgba(0,0,0,0.35)]"
          style={{
            background: 'var(--gs-panel)',
            color: 'var(--gs-ink)',
            borderColor: 'var(--gs-line)',
            animation: 'gs-slide 220ms ease both',
            fontFamily: 'inherit',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <header
            className="flex shrink-0 items-center gap-3 border-b px-[22px] py-4"
            style={{ borderColor: 'var(--gs-line)' }}
          >
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              onBlur={commitName}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
              aria-label={t('drawer.nameLabel')}
              title={t('drawer.renameHint')}
              className="-ms-2 min-w-0 flex-1 rounded-lg border border-transparent px-2 py-1.5 text-xl font-extrabold tracking-[-0.02em] outline-none focus:border-[var(--gs-accent)]"
              style={{
                color: 'var(--gs-ink)',
                background: 'transparent',
                fontFamily: 'inherit',
              }}
            />
            <button
              type="button"
              title={t('common:actions.close')}
              onClick={onClose}
              className="grid shrink-0 place-items-center p-[5px]"
              style={{ color: 'var(--gs-muted)', background: 'none', border: 'none' }}
            >
              <svg
                width="19"
                height="19"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden
              >
                <path d="m6 6 12 12M18 6 6 18" />
              </svg>
            </button>
          </header>

          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-[22px]">
            <LayoutPreviewGrid def={def} labels={previewLabels} big />

            <div className="flex flex-col gap-3.5">
              <span className="text-[17px] font-extrabold tracking-[-0.015em]">
                {t('drawer.zonesTitle')}
              </span>
              {def.zones
                .filter((zone) => !zone.ticker)
                .map((zone) => {
                  const value =
                    layout.zones.find((item) => item.key === zone.key)?.playlistId ?? '';
                  return (
                    <label key={zone.key} className="flex flex-col gap-[7px]">
                      <span className="text-[13px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
                        {zoneName(zone.key)}
                      </span>
                      <select
                        value={value}
                        disabled={save.isPending}
                        onChange={(event) => {
                          const next = event.target.value || null;
                          save.mutate(
                            { zones: { [zone.key]: next } },
                            {
                              onSuccess: () => {
                                if (next) {
                                  setToast(
                                    t('drawer.zoneAssigned', {
                                      zone: zoneName(zone.key),
                                      playlist:
                                        playlistName(next) || t('drawer.playlistFallback'),
                                    }),
                                  );
                                }
                              },
                            },
                          );
                        }}
                        className="rounded-[10px] border px-3 py-[11px] text-[14.5px] outline-none focus:border-[var(--gs-accent)]"
                        style={{
                          color: 'var(--gs-ink)',
                          background: 'var(--gs-field)',
                          borderColor: 'var(--gs-line)',
                          fontFamily: 'inherit',
                        }}
                      >
                        <option value="">{t('drawer.choosePlaylist')}</option>
                        {playlists.map((playlist) => (
                          <option key={playlist.id} value={playlist.id}>
                            {playlist.name || t('drawer.untitledPlaylist')}
                          </option>
                        ))}
                      </select>
                    </label>
                  );
                })}

              {hasTicker ? (
                <label className="flex flex-col gap-[7px]">
                  <span className="text-[13px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
                    {t('drawer.tickerLabel')}
                  </span>
                  <input
                    value={ticker}
                    onChange={(event) => setTicker(event.target.value)}
                    onBlur={commitTicker}
                    placeholder={t('drawer.tickerPlaceholder')}
                    className="rounded-[10px] border px-3 py-[11px] text-[14.5px] outline-none focus:border-[var(--gs-accent)]"
                    style={{
                      color: 'var(--gs-ink)',
                      background: 'var(--gs-field)',
                      borderColor: 'var(--gs-line)',
                      fontFamily: 'inherit',
                    }}
                  />
                </label>
              ) : null}
            </div>

            {error ? (
              <p className="m-0 text-sm font-semibold" style={{ color: 'var(--gs-bad)' }}>
                {error}
              </p>
            ) : null}
          </div>

          <footer
            className="flex shrink-0 items-center gap-3 border-t px-[22px] py-3.5"
            style={{ borderColor: 'var(--gs-line)' }}
          >
            <span className="flex-1 text-[12.5px]" style={{ color: 'var(--gs-muted)' }}>
              {t('drawer.autosaved')}
            </span>
            <button
              type="button"
              disabled={remove.isPending}
              onClick={() => setConfirmDelete(true)}
              className="rounded-[10px] border px-3.5 py-2.5 text-[13.5px] font-semibold disabled:opacity-50"
              style={{
                color: 'var(--gs-bad)',
                background: 'none',
                borderColor: 'var(--gs-line)',
                fontFamily: 'inherit',
              }}
            >
              {t('common:actions.delete')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-[10px] border-none px-[22px] py-[11px] text-sm font-bold"
              style={{
                color: 'var(--gs-accent-ink)',
                background: 'var(--gs-accent-solid)',
                fontFamily: 'inherit',
              }}
            >
              {t('common:actions.done')}
            </button>
          </footer>
        </aside>
      </div>

      {toast ? (
        <div
          className="fixed bottom-4 left-4 right-4 z-[110] flex items-center justify-center gap-2.5 rounded-xl border px-4 py-3 text-sm font-semibold shadow-[0_16px_40px_rgba(0,0,0,0.35)] sm:bottom-7 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:px-[18px]"
          style={{
            color: 'var(--gs-ink)',
            background: 'var(--gs-panel)',
            borderColor: 'var(--gs-line)',
            animation: 'gs-in 200ms ease both',
          }}
        >
          <span className="h-2 w-2 rounded-full" style={{ background: 'var(--gs-ok)' }} />
          {toast}
        </div>
      ) : null}

      {confirmDelete ? (
        <ConfirmDialog
          title={t('drawer.confirmDelete.title', { name: layout.name })}
          description={t('drawer.confirmDelete.description')}
          confirmLabel={t('drawer.confirmDelete.confirm')}
          tone="danger"
          confirming={remove.isPending}
          onCancel={() => {
            if (!remove.isPending) setConfirmDelete(false);
          }}
          onConfirm={() => remove.mutate()}
        />
      ) : null}
    </>,
    document.body,
  );
}
